import { useEffect, useRef, useState } from "react"
import { MapPin, Phone, Navigation, X } from "lucide-react"
import { Card, CardContent } from "./ui/card"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"

export function MapContainer({ stations, onStationSelect }) {
  const mapRef = useRef(null)
  const [map, setMap] = useState(null)
  const [markers, setMarkers] = useState([])
  const [selectedStation, setSelectedStation] = useState(null)
  const [leafletLoaded, setLeafletLoaded] = useState(false)

  // Load Leaflet dynamically
  useEffect(() => {
    if (typeof window === "undefined") return

    // Load Leaflet CSS
    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
    document.head.appendChild(link)

    // Load Leaflet JS
    const script = document.createElement("script")
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"
    script.onload = () => setLeafletLoaded(true)
    document.body.appendChild(script)

    return () => {
      if (document.head.contains(link)) document.head.removeChild(link)
      if (document.body.contains(script)) document.body.removeChild(script)
    }
  }, [])

  // Initialize map
  useEffect(() => {
    if (!leafletLoaded || !mapRef.current || map) return

    const L = window.L
    if (!L) return

    // Center on Malabo, Equatorial Guinea
    const newMap = L.map(mapRef.current).setView([3.7523, 8.7741], 13)

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(newMap)

    setMap(newMap)
  }, [leafletLoaded, map])

  // Add markers
  useEffect(() => {
    if (!map || !leafletLoaded) return

    const L = window.L
    if (!L) return

    // Clear existing markers
    markers.forEach((marker) => marker.remove())

    // Create custom icons
    const getIcon = (stock) => {
      const color = stock === "available" ? "#22c55e" : stock === "low" ? "#eab308" : "#ef4444"
      return L.divIcon({
        className: "custom-marker",
        html: `<div style="background-color: ${color}; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
        </div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      })
    }

    // Add new markers
    const newMarkers = stations.map((station) => {
      const marker = L.marker([station.latitude, station.longitude], {
        icon: getIcon(station.stock),
      }).addTo(map)

      marker.on("click", () => {
        setSelectedStation(station)
        onStationSelect(station)
        map.setView([station.latitude, station.longitude], 15)
      })

      return marker
    })

    setMarkers(newMarkers)
  }, [map, stations, leafletLoaded, onStationSelect])

  const getStockColor = (stock) => {
    switch (stock) {
      case "available":
        return "bg-green-500"
      case "low":
        return "bg-yellow-500"
      case "out":
        return "bg-red-500"
      default:
        return "bg-gray-500"
    }
  }

  const getStockText = (stock) => {
    switch (stock) {
      case "available":
        return "Disponible"
      case "low":
        return "Stock Bajo"
      case "out":
        return "Agotado"
      default:
        return "Desconocido"
    }
  }

  return (
    <div className="relative w-full h-[600px]">
      <div ref={mapRef} className="w-full h-full rounded-lg" />

      {/* Selected Station Info Card */}
      {selectedStation && (
        <Card className="absolute bottom-4 left-4 right-4 md:right-auto md:w-80 shadow-lg z-[1000]">
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">{selectedStation.name}</h3>
                <Badge className={`${getStockColor(selectedStation.stock)} text-white`}>
                  {getStockText(selectedStation.stock)}
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => {
                  setSelectedStation(null)
                  onStationSelect(null)
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-2 mb-3">
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">{selectedStation.address}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-muted-foreground">{selectedStation.phone}</span>
              </div>
              {(selectedStation.stock_espanol !== undefined || selectedStation.stock_francesa !== undefined) && (
                <div className="text-sm text-muted-foreground pt-2 border-t">
                  <div className="flex justify-between">
                    <span>Española:</span>
                    <span className="font-medium">{selectedStation.stock_espanol || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Francesa:</span>
                    <span className="font-medium">{selectedStation.stock_francesa || 0}</span>
                  </div>
                </div>
              )}
            </div>

            <Button
              size="sm"
              className="w-full bg-[#0288d0] hover:bg-[#0288d0]/90"
              onClick={() => {
                window.open(
                  `https://www.google.com/maps/dir/?api=1&destination=${selectedStation.latitude},${selectedStation.longitude}`,
                  "_blank",
                )
              }}
            >
              <Navigation className="h-4 w-4 mr-2" />
              Obtener Direcciones
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading overlay */}
      {!leafletLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 rounded-lg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0288d0] mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Cargando mapa...</p>
          </div>
        </div>
      )}
    </div>
  )
}